import { FormattedNumber } from "react-intl";
import type { ReactNode } from "react";
import { Link } from "react-router";

// Shared dashboard primitives used by Home's overview tiles.

// One "+ …" quick-action line, shared by the hover-reveal overlays.
export const QUICK_ACTION_CLASS =
  "-mx-1 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-smycles-meta text-ink transition hover:bg-ink/[0.06] hover:text-ink-accent";

export function QuickActionLink({
  id,
  to,
  children,
  icon = "+",
  className = "",
}: {
  id?: string;
  to: string;
  children: ReactNode;
  icon?: ReactNode;
  // Layout-only additions (e.g. mt-auto to pin the link to the bottom of a taller container) —
  // appended after QUICK_ACTION_CLASS, not a replacement for it.
  className?: string;
}) {
  return (
    <Link id={id} to={to} className={`${QUICK_ACTION_CLASS} ${className}`}>
      <span className="text-ink-muted">{icon}</span>
      {children}
    </Link>
  );
}

// Hover reveal lifted verbatim from PowerTile (components/design/PowerTile.tsx): artwork sits
// behind the tile content, hidden + blurred until hover. Needs a `group` + `relative
// overflow-hidden` ancestor, and to be the FIRST child so later siblings paint on top of it
// without an explicit z-index.
export function HeroRevealImage({ src }: { src: string }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]" aria-hidden>
      <img
        src={src}
        alt=""
        decoding="async"
        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-0 blur-md transition-[transform,opacity,filter] duration-500 ease-out group-hover:scale-100 group-hover:opacity-100 group-hover:blur-none"
      />
      {/* Keep the artwork wash neutral when the configured page colour changes. */}
      <div className="absolute inset-0 bg-white/80 opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100" />
    </div>
  );
}

// The thin percent bar StatTile and SplitStatTile both draw. Local to this file rather than its
// own module: it is two divs and a width, and this IS the shared dashboard-primitives file.
function StatBar({ value }: { value: number }) {
  return (
    <div className="flex h-full items-center gap-2">
      <div className="h-1.5 min-w-0 flex-1 rounded-full bg-ink/10">
        <div className="h-full rounded-full bg-accent" style={{ width: `${value}%` }} />
      </div>
      <span className="text-smycles-micro tabular-nums text-ink">
        <FormattedNumber value={value / 100} style="percent" maximumFractionDigits={1} />
      </span>
    </div>
  );
}

// Compact dashboard tile: a KPI (number, optionally with a progress bar) that
// doubles as a quick jump. When `actions` are passed they stay collapsed and
// expand on hover / keyboard focus. The progress-bar row is always reserved so
// a tile WITH a bar isn't taller than one without, keeps tiles uniform.
export function StatTile({
  id,
  to,
  label,
  value,
  sub,
  bar,
  loading,
  actions,
  heroImageSrc,
}: {
  // Testanker; `${id}-value` sitzt auf der Zahl.
  id?: string;
  to: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  bar?: number;
  loading?: boolean;
  actions?: ReactNode;
  // Section artwork (e.g. /heroes/save.jpg), revealed on hover. Demo-only polish (spark home page);
  // omit for tiles that shouldn't carry it.
  heroImageSrc?: string;
}) {
  return (
    // min-h-36, not -32: the row uses items-start (a hover-expanded tile must not stretch its
    // siblings — see Row/the grid comments in HomePage.tsx), so this floor is what keeps every
    // tile in a row the same collapsed height, not just a lower bound. StatTile's own content
    // (text-smycles-display-xl value + the always-reserved bar row below) already clears -32 on its own; -36 is
    // sized to also cover SplitTileShell's shorter content (a text-smycles-display-m value, no bar row) so both
    // land on the same floor instead of the split tiles reading visibly shorter (spark home page
    // feedback).
    <div
      id={id}
      className="box group relative flex min-h-36 flex-col overflow-hidden bg-surface p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:ring-ink/15"
    >
      {heroImageSrc ? <HeroRevealImage src={heroImageSrc} /> : null}
      {/* relative, NOT absolute: the hero image is absolutely positioned, and an absolutely
          positioned sibling always paints above a static one regardless of DOM order (CSS stacking
          rules) — without this wrapper the image sat on top of the text. `relative` moves this
          block into the same "positioned" paint group, where DOM order (after the image) wins,
          while staying in normal flow so the tile still grows with its content. */}
      <div className="relative flex flex-1 flex-col">
        <Link to={to} className="flex flex-col">
          {/* smycles-field-label, not an ad-hoc text-smycles-micro: UI_CONVENTIONS' field type scale — the
              canonical eyebrow-above-a-value size (11px), not a size outside that scale. */}
          <div className="smycles-field-label">{label}</div>
          {/* The tile's hero value uses the established text-smycles-display-xl display size. */}
          <div id={id && `${id}-value`} className="mt-2 font-serif text-smycles-display-xl leading-none tabular-nums">
            {loading ? <span className="text-ink-muted">…</span> : value}
          </div>
          {/* Always reserve the progress-bar row so a tile WITH a bar isn't taller
              than one without, keeps every tile a uniform height. Invisible when
              no bar is passed. */}
          <div className="mt-2 h-5 w-full">
            {typeof bar === "number" ? <StatBar value={bar} /> : null}
          </div>
        </Link>
        {/* sub sits OUTSIDE the tile link so it can carry its own inline links
            (nested <a> would be invalid). */}
        {sub ? <div className="mt-1.5 text-smycles-label leading-snug text-ink-muted">{sub}</div> : null}
        {actions ? (
          // Actions expand the tile inline on hover / keyboard focus: grid-rows
          // 0fr→1fr animates to the exact content height, so the tile grows to fit.
          <div className="grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity,margin-top] duration-500 ease-out group-hover:mt-3 group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:mt-3 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
            <div className="flex min-h-0 flex-col gap-0.5 overflow-hidden">{actions}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// Shared frame for a tile split into two independently-linked halves: the box, hero-image reveal,
// the two-column grid and its centred divider (an explicit line rather than divide-x, so
// content-length differences between the halves — a one-line vs. three-line sub — can't read as
// if it drifted off-centre). SplitStatTile (below) fills it with the KPI shape (value + sub +
// hover-link); other callers needing two independently-linked columns of arbitrary content (e.g.
// two power lists side by side) pass their own.
export function SplitTileShell({
  id,
  left,
  right,
  heroImageSrc,
}: {
  // Testanker; sitzt auf dem Kasten, wie bei StatTile.
  id?: string;
  left: ReactNode;
  right: ReactNode;
  heroImageSrc?: string;
}) {
  return (
    // min-h-36, not -32: the row uses items-start (a hover-expanded tile must not stretch its
    // siblings — see Row/the grid comments in HomePage.tsx), so this floor is what keeps every
    // tile in a row the same collapsed height, not just a lower bound. StatTile's own content
    // (text-smycles-display-xl value + the always-reserved bar row below) already clears -32 on its own; -36 is
    // sized to also cover SplitTileShell's shorter content (a text-smycles-display-m value, no bar row) so both
    // land on the same floor instead of the split tiles reading visibly shorter (spark home page
    // feedback).
    <div
      id={id}
      className="box group relative flex min-h-36 flex-col overflow-hidden bg-surface p-4 transition-[transform,box-shadow] hover:-translate-y-0.5 hover:ring-ink/15"
    >
      {heroImageSrc ? <HeroRevealImage src={heroImageSrc} /> : null}
      {/* Side by side, not stacked: stacking two halves would make this tile roughly twice the
          height of its StatTile neighbours in the same grid row. */}
      <div className="relative grid flex-1 grid-cols-2">
        <div
          className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-ink/10"
          aria-hidden="true"
        />
        <div className="flex flex-col pr-4">{left}</div>
        <div className="flex flex-col pl-4">{right}</div>
      </div>
    </div>
  );
}

// One half of a SplitStatTile. Named rather than written out per side: the two halves are the
// same shape by construction, and halfContent() below already depends on that.
export interface SplitStatHalf {
  to: string;
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  // 0-100. Omit when there is no percentage to show at all - that is not the same as 0 %, which
  // would draw an empty track and read as "none of it done yet".
  bar?: number;
  // While true the half shows the same placeholder StatTile does, in place of value AND bar.
  // Lives here rather than at the callsite so a caller never has to know what "loading" looks
  // like - same reason StatTile takes a `loading` flag instead of a pre-rendered node.
  loading?: boolean;
  // Omit when the half has nothing worth acting on, e.g. a percentage that does not exist yet.
  // The half then shows no link at all rather than offering a click that goes nowhere useful.
  linkLabel?: string;
}

// Two stats sharing one tile, each half its own link — for merging two low-traffic KPIs (e.g.
// Community + Pins) back to a single grid slot without losing either number. Not a variant of
// StatTile (single value, single link): different shape, so its own component per this folder's
// "one component per file, small internal helpers stay local" rule doesn't apply here since this
// IS the shared dashboard-primitives file.
export function SplitStatTile({
  id,
  left,
  right,
  heroImageSrc,
}: {
  id?: string;
  left: SplitStatHalf;
  right: SplitStatHalf;
  heroImageSrc?: string;
}) {
  const halfContent = (h: SplitStatHalf) => (
    <>
      <div className="smycles-field-label">{h.label}</div>
      {/* One step below StatTile's text-smycles-display-xl, same ratio as before (text-smycles-title was one step below
          StatTile's old text-smycles-display-m): a half-width column has roughly half the horizontal room a
          full tile does. */}
      <div className="mt-1 font-serif text-smycles-display-m leading-none tabular-nums">
        {h.loading ? <span className="text-ink-muted">…</span> : h.value}
      </div>
      {/* Hidden while loading: a 0 %-filled track under a "…" reads as real progress. And unlike
          StatTile this row is NOT reserved when absent - a half either is a percentage or isn't,
          and the halves of one tile are always the same kind, so there is no uniform-height floor
          to hold here. */}
      {!h.loading && typeof h.bar === "number" ? (
        <div className="mt-2 h-5 w-full">
          <StatBar value={h.bar} />
        </div>
      ) : null}
      {h.sub ? <div className="mt-1 text-smycles-label leading-snug text-ink-muted">{h.sub}</div> : null}
      {/* Same expand-on-hover mechanic as StatTile's own `actions` (grid-rows 0fr→1fr), but
          mt-auto instead of a fixed mt-3: the two links sit bottom-aligned with each other (spark
          home page feedback), not each pinned a fixed distance below its own differently-long sub
          text. The explicit link, not the whole half, stays a QuickActionLink rather than nesting
          it in a wrapping Link (invalid nested <a>). The outer (unnamed) group — same as
          StatTile — means hovering ANYWHERE on the tile reveals both links together. */}
      {h.linkLabel ? (
        <div className="mt-auto grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-500 ease-out group-hover:grid-rows-[1fr] group-hover:opacity-100 group-focus-within:grid-rows-[1fr] group-focus-within:opacity-100">
          <div className="min-h-0 overflow-hidden">
            <QuickActionLink to={h.to} icon="→">
              {h.linkLabel}
            </QuickActionLink>
          </div>
        </div>
      ) : null}
    </>
  );
  return (
    <SplitTileShell
      id={id}
      heroImageSrc={heroImageSrc}
      left={halfContent(left)}
      right={halfContent(right)}
    />
  );
}
